/**
 * Pure server-pagination helpers (Requirements 6.1-6.5).
 *
 * The arithmetic and the payload extraction that were inlined in
 * `PharmaDashboard/services/api.js#getStockistPerformance` and in
 * `StockistTable.jsx` live here instead, so page counts, clamping, ranges, and the
 * label can be reasoned about (and property-tested) without a network or a render.
 *
 * Nothing here performs I/O or reads the environment.
 */

/** Coerces numbers and numeric strings; anything else (null, NaN, {}) → null. */
function toFiniteNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Non-negative integer count, or null when the value is not usable. */
function toCount(value) {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  return Math.max(0, Math.floor(numeric));
}

/** Positive integer page size, or null when the value is not usable. */
function toPageSize(value) {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  const floored = Math.floor(numeric);
  return floored > 0 ? floored : null;
}

/**
 * Number of pages needed to show `total` records at `pageSize` per page.
 *
 * Always at least 1: an empty result set is still "page 1 of 1", which is what keeps
 * the pagination label renderable for zero records (Req 6.5). A missing or
 * non-positive page size means "everything on one page" rather than a division by
 * zero.
 *
 * @param {number|string|null|undefined} total
 * @param {number|string|null|undefined} pageSize
 * @returns {number} integer >= 1
 */
export function totalPages(total, pageSize) {
  const records = toCount(total) ?? 0;
  const size = toPageSize(pageSize);
  if (size === null) return 1;
  return Math.max(1, Math.ceil(records / size));
}

/**
 * Brings a requested page number into `[1, totalPages(total, pageSize)]`.
 *
 * Zero, negative values, fractions, non-numeric input, and values past the last page
 * all resolve to a page that actually exists (Req 6.4).
 *
 * @param {number|string|null|undefined} page
 * @param {number|string|null|undefined} total
 * @param {number|string|null|undefined} pageSize
 * @returns {number} integer in [1, totalPages]
 */
export function clampPage(page, total, pageSize) {
  const last = totalPages(total, pageSize);
  const requested = toFiniteNumber(page);
  if (requested === null) return 1;
  return Math.min(Math.max(Math.floor(requested), 1), last);
}

/**
 * 1-based record range shown on a page, for "Showing 26-50" style labels.
 *
 * `to` is clamped to `total`, so the last page reports only the records it holds. A
 * page that holds nothing (zero records, or a page past the end) comes back with
 * `to < from`; that is the empty marker, and it makes the ranges of pages 1..N tile
 * the records 1..total exactly once with no overlap and no gap (Req 6.3).
 *
 * @param {number|string|null|undefined} page
 * @param {number|string|null|undefined} pageSize
 * @param {number|string|null|undefined} total
 * @returns {{ from: number, to: number }}
 */
export function pageRange(page, pageSize, total) {
  const records = toCount(total) ?? 0;
  const size = toPageSize(pageSize);
  const current = clampPage(page, records, size);

  if (size === null) {
    return { from: 1, to: records };
  }

  const from = (current - 1) * size + 1;
  const to = Math.min(current * size, records);
  return { from, to };
}

/**
 * Reads a paginated backend payload.
 *
 * Two shapes are accepted: the current `{ rows, total, page, page_size }` object
 * (Req 6.2) and the legacy flat array some endpoints still return. Rows come back as
 * an array in original order — never `undefined` — and `total` / `page` / `pageSize`
 * fall back to the values the caller requested when the payload omits them
 * (Req 6.3). Mapping the rows stays the caller's job; nothing here reshapes a row.
 *
 * @param {{ rows?: unknown[], total?: number, page?: number, page_size?: number }|unknown[]|null|undefined} payload
 * @param {{ page?: number, pageSize?: number }} [request] the requested page/size, echoed on fallback
 * @returns {{ rows: unknown[], total: number, page: number, pageSize: number }}
 */
export function extractPaginatedPayload(payload, { page, pageSize } = {}) {
  const requestedPage = toCount(page) ?? 1;
  const requestedPageSize = toPageSize(pageSize);

  const isFlatArray = Array.isArray(payload);
  const isEnvelope = !isFlatArray && payload !== null && typeof payload === "object";

  const rows = isFlatArray
    ? payload
    : isEnvelope && Array.isArray(payload.rows)
      ? payload.rows
      : [];

  const total = (isEnvelope ? toCount(payload.total) : null) ?? rows.length;
  const resolvedPage = (isEnvelope ? toCount(payload.page) : null) ?? requestedPage;
  const resolvedPageSize =
    (isEnvelope ? toPageSize(payload.page_size) : null) ??
    requestedPageSize ??
    (rows.length > 0 ? rows.length : 1);

  return { rows, total, page: resolvedPage, pageSize: resolvedPageSize };
}

/** Grouped record count, falling back to plain digits if Intl is unavailable. */
function formatCount(value) {
  try {
    return value.toLocaleString("en-IN");
  } catch {
    return String(value);
  }
}

/**
 * One-line pagination summary: current page, total pages, and record count.
 *
 * Rendered unconditionally by `StockistTable` — including for a single page and for
 * zero records — so those three numbers are always visible (Req 6.5).
 *
 * @param {{ page?: number, pageSize?: number, total?: number }} [view]
 * @returns {string} e.g. `"Page 2 of 7 · 1,340 records"`
 */
export function paginationLabel({ page, pageSize, total } = {}) {
  const records = toCount(total) ?? 0;
  const last = totalPages(records, pageSize);
  const current = clampPage(page, records, pageSize);
  return `Page ${current} of ${last} · ${formatCount(records)} records`;
}
